import { Device, type types as ms } from 'mediasoup-client';
import type { ChatMessage, HelloResult, KeyConfig, Presence, TransportParams } from '@bccomms/shared';
import { allowedTargets, chTarget, parseTarget } from '@bccomms/shared';
import { AudioEngine, dbToGain, openInterfaceMic, openMic, openProgramInput } from './audio';
import { apiBase, configureNativeAudio, desktop, isDesktop, isNative, keepNativeAlive, nativePlatform } from './native';
import { RpcClient, wsUrl } from './rpc';
import { getState, keyVolume, persistArmed, setState, updateSettings, useStore } from '../store';

const TOKEN_KEY = 'bccomms.token';
const CALL_FLASH_MS = 4000;

function describeClient(): string {
  if (isNative()) return `${nativePlatform() === 'ios' ? 'iOS' : 'Android'} · app`;
  const d = desktop();
  if (d) return `${d.platform === 'darwin' ? 'macOS' : d.platform === 'win32' ? 'Windows' : 'Linux'} · app`;
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'web';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'browser';
  return `${os} · ${browser}`;
}

/**
 * Session — the one stateful object behind the UI. Owns the RPC socket, the mediasoup Device,
 * transports, producers and consumers, and pushes everything into the zustand store.
 */
class Session {
  readonly audio = new AudioEngine();
  private rpc: RpcClient | null = null;
  private device: Device | null = null;
  private sendT: ms.Transport | null = null;
  private recvT: ms.Transport | null = null;
  private micStream: MediaStream | null = null;
  /** Releases native interface capture (desktop app) when the mic is an interface channel. */
  private micRelease: (() => void) | null = null;
  private micProducer: ms.Producer | null = null;
  private readonly programProducers = new Map<string, { producer: ms.Producer; stream: MediaStream }>();
  private readonly consumers = new Map<string, ms.Consumer>();
  private reconnectTimer: number | null = null;
  private reconnectDelay = 1000;
  private intentionalClose = false;
  private meterTimer: number | null = null;
  private pingTimer: number | null = null;
  private wakeLock: { release: () => Promise<void> } | null = null;
  private unsub: Array<() => void> = [];
  /**
   * Reference-counted talk arbitration. Several independent sources — manual PTT, a latched key,
   * the dial's centre button (held or in continuous mode), and now VOX — can all want the same
   * target talking at once. A target only goes silent once every source that asked for it has let
   * go, so e.g. a VOX hang-release never cuts audio a manual PTT press is still holding.
   */
  private readonly talkSources = new Map<string, Set<string>>();
  private readonly voxState = new Map<string, { above: boolean; releaseTimer: number | null }>();

  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  // ---- auth ---------------------------------------------------------------------------------------

  async login(username: string, password: string): Promise<void> {
    const res = await fetch(`${apiBase()}/api/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const body = (await res.json()) as { token?: string; error?: string };
    if (!res.ok || !body.token) throw new Error(body.error ?? 'login failed');
    localStorage.setItem(TOKEN_KEY, body.token);
    await this.connect();
  }

  logout(): void {
    this.intentionalClose = true;
    localStorage.removeItem(TOKEN_KEY);
    void keepNativeAlive(false);
    this.teardownMedia();
    this.stopMicStream();
    this.rpc?.close();
    this.rpc = null;
    setState({ conn: 'idle', user: null, panel: null, presence: {}, chat: [], talking: [], listening: [], latched: [], kicked: null, view: 'panel', drawer: 'none' });
  }

  // ---- connection ----------------------------------------------------------------------------------

  /**
   * Open the socket and join. After a kick ("signed in elsewhere") we stay offline until the user
   * explicitly reconnects (`force`), so two devices sharing one login cannot kick each other in a loop.
   */
  /** Set once the saved-token sign-in has been tried at startup, so a failure isn't retried in a loop. */
  autoConnectTried = false;

  async connect(force = false): Promise<void> {
    const token = this.token;
    if (!token) return;
    if (getState().kicked && !force) return;
    if (this.rpc && this.rpc.state !== 'closed') return; // already connecting / connected
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.intentionalClose = false;
    setState((s) => ({ conn: s.user ? 'reconnecting' : 'connecting', connError: null, kicked: null }));

    const rpc = new RpcClient(wsUrl());
    this.rpc = rpc;
    this.bindEvents(rpc);
    rpc.onStateChange = (st) => {
      if (st === 'closed' && this.rpc === rpc) this.onDisconnected();
    };
    try {
      await rpc.connect();
      const hello = (await rpc.call('hello', { token, client: describeClient() })) as HelloResult;
      this.applyHello(hello);
      window.scrollTo(0, 0);
      this.reconnectDelay = 1000;
      setState({ conn: 'online', connError: null });
      await this.startMedia(hello.routerRtpCapabilities as ms.RtpCapabilities);
      await this.restoreState();
      this.startTimers();
      void this.requestWakeLock();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (/token/i.test(message)) {
        localStorage.removeItem(TOKEN_KEY);
        setState({ conn: 'idle', connError: 'Session expired — please sign in again', user: null });
        rpc.close();
        return;
      }
      setState({ connError: message });
      rpc.close();
    }
  }

  private onDisconnected(): void {
    this.stopTimers();
    this.teardownMedia();
    // Never signed in this session (a saved token against an unreachable server): stay on the login
    // form with the error instead of cycling between it and the connecting screen.
    if (this.intentionalClose || !this.token || getState().kicked || !getState().user) {
      setState({ conn: 'idle' });
      return;
    }
    setState({ conn: 'reconnecting' });
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 1.7, 10_000);
  }

  private applyHello(h: HelloResult): void {
    const presence: Record<string, Presence> = {};
    for (const p of h.presence) presence[p.userId] = p;
    const me = presence[h.user.id];
    setState({
      serverName: h.serverName,
      user: h.user,
      panel: h.panel,
      channels: h.channels,
      users: h.users,
      presence,
      chat: h.chat,
      listening: me?.listening ?? [],
      talking: [],
      feeding: [],
      consumers: {},
      routes: {},
    });
    document.title = `${h.serverName} · ${h.user.displayName}`;
  }

  /** After (re)connect: push latched talk keys and non-default listen state back to the server. */
  private async restoreState(): Promise<void> {
    const s = getState();
    const panel = s.panel;
    if (!panel || !this.rpc) return;
    const wantListen = new Set<string>();
    for (const key of panel.keys) {
      const stored = localStorage.getItem(`bccomms.listen.${key.id}`);
      const on = key.listen === 'always' ? true : stored === null ? key.listenDefault : stored === '1';
      if (on && key.listen !== 'none') for (const t of key.targets) wantListen.add(t);
    }
    const current = new Set(s.listening);
    const add = [...wantListen].filter((t) => !current.has(t));
    const remove = [...current].filter((t) => !wantListen.has(t));
    if (add.length) await this.rpc.call('listen.set', { targets: add, on: true });
    if (remove.length) await this.rpc.call('listen.set', { targets: remove, on: false });
    setState({ listening: [...wantListen] });
    for (const keyId of s.latched) {
      const key = panel.keys.find((k) => k.id === keyId);
      if (key) this.wantTalk(`latch:${keyId}`, key.targets);
    }
    if (s.continuous) {
      const targets = this.armedTargets();
      if (targets.length) this.wantTalk('dial-center', targets);
    }
    await this.flushTalk();
  }

  // ---- media ----------------------------------------------------------------------------------------

  private async startMedia(routerRtpCapabilities: ms.RtpCapabilities): Promise<void> {
    const rpc = this.rpc;
    if (!rpc) return;
    setState({ media: 'starting', mediaError: null });
    try {
      await this.audio.ensure();
      if (isNative()) await configureNativeAudio(getState().settings.speaker);
      await this.audio.setSink(getState().settings.outputDeviceId);
      this.audio.setMultichannel(isDesktop() && getState().settings.multichannelOut);
      this.audio.setMasterVolume(getState().settings.masterVolume);

      const device = new Device();
      await device.load({ routerRtpCapabilities });
      this.device = device;

      const [sendParams, recvParams] = await Promise.all([
        rpc.call('transport.create', { direction: 'send' }) as Promise<TransportParams>,
        rpc.call('transport.create', { direction: 'recv' }) as Promise<TransportParams>,
      ]);
      this.sendT = this.wireTransport(device.createSendTransport(sendParams as ms.TransportOptions));
      this.recvT = this.wireTransport(device.createRecvTransport(recvParams as ms.TransportOptions));

      await this.openMicAndProduce();
      await rpc.call('ready', { rtpCapabilities: device.rtpCapabilities });
      setState({ media: this.micProducer ? 'ready' : 'nomic' });
      // After the mic prompt: Android only allows a microphone foreground service once it's granted.
      void keepNativeAlive(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('media start failed', err);
      setState({ media: 'error', mediaError: message });
    }
  }

  private wireTransport(t: ms.Transport): ms.Transport {
    t.on('connect', ({ dtlsParameters }, cb, eb) => {
      this.rpc
        ?.call('transport.connect', { transportId: t.id, dtlsParameters })
        .then(() => cb())
        .catch(eb);
    });
    if (t.direction === 'send') {
      t.on('produce', ({ kind, rtpParameters, appData }, cb, eb) => {
        this.rpc
          ?.call('produce', { transportId: t.id, kind: kind as 'audio', rtpParameters, appData: appData as { role: 'mic' } })
          .then((r) => cb({ id: (r as { id: string }).id }))
          .catch(eb);
      });
    }
    t.on('connectionstatechange', (state) => {
      if (state === 'failed' || state === 'disconnected') {
        console.warn(`${t.direction} transport ${state}`);
        if (state === 'failed') setState({ media: 'error', mediaError: `Media transport ${state} — check firewall/UDP port` });
      }
    });
    return t;
  }

  private async openMicAndProduce(): Promise<void> {
    const s = getState().settings;
    if (!this.micStream || !this.micStream.active) {
      this.stopMicStream();
      try {
        const bridge = desktop();
        if (bridge && s.micInterface) {
          // Browsers can't capture past channel 2, so the desktop app captures the interface natively.
          const mic = await openInterfaceMic(await this.audio.ensure(), bridge, s.micInterface.device, s.micInterface.channel);
          this.micStream = mic.stream;
          this.micRelease = mic.stop;
        } else {
          this.micStream = await openMic({
            deviceId: s.micDeviceId,
            echoCancellation: s.echoCancellation,
            noiseSuppression: s.noiseSuppression,
            autoGainControl: s.autoGainControl,
          });
        }
      } catch (err) {
        console.warn('microphone unavailable — listen-only', err);
        setState({ mediaError: 'Microphone unavailable — listen only' });
        return;
      }
    }
    this.audio.attachMic(this.micStream);
    this.audio.setSidetone(s.sidetone);
    const track = this.micStream.getAudioTracks()[0];
    if (!this.sendT || !track) return;
    this.micProducer = await this.sendT.produce({
      track,
      codecOptions: { opusStereo: false, opusDtx: true, opusFec: true, opusMaxAverageBitrate: 40_000 },
      appData: { role: 'mic' },
    });
    if (getState().talking.length === 0) this.micProducer.pause();
    this.micProducer.on('transportclose', () => {
      this.micProducer = null;
    });
  }

  /** Re-open the microphone with new device/processing settings. */
  async restartMic(): Promise<void> {
    this.micProducer?.close();
    this.micProducer = null;
    this.stopMicStream();
    if (!this.sendT) return;
    await this.openMicAndProduce();
    const producer = this.micProducer as ms.Producer | null;
    setState({ media: producer ? 'ready' : 'nomic' });
    if (getState().talking.length) producer?.resume();
  }

  private stopMicStream(): void {
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.micRelease?.();
    this.micRelease = null;
  }

  private teardownMedia(): void {
    for (const [id] of this.consumers) this.dropConsumer(id);
    for (const [channelId, p] of this.programProducers) {
      p.producer.close();
      p.stream.getTracks().forEach((t) => t.stop());
      this.programProducers.delete(channelId);
    }
    this.micProducer?.close();
    this.micProducer = null;
    this.sendT?.close();
    this.recvT?.close();
    this.sendT = null;
    this.recvT = null;
    this.device = null;
    setState({ media: 'idle', consumers: {}, routes: {}, feeding: [] });
  }

  // ---- events ---------------------------------------------------------------------------------------

  private bindEvents(rpc: RpcClient): void {
    for (const u of this.unsub) u();
    this.unsub = [
      rpc.on('consumer.new', (ev) => void this.onConsumerNew(ev)),
      rpc.on('consumer.route', ({ id, targets }) => {
        setState((s) => ({ routes: { ...s.routes, [id]: targets } }));
        this.audio.setRouted(id, targets.length > 0);
        this.refreshGains();
      }),
      rpc.on('consumer.closed', ({ id }) => this.dropConsumer(id)),
      rpc.on('presence', ({ list }) => {
        const presence: Record<string, Presence> = {};
        for (const p of list) presence[p.userId] = p;
        setState({ presence });
        this.refreshGains();
      }),
      rpc.on('presence.update', ({ p }) => {
        setState((s) => {
          const next = { ...s.presence, [p.userId]: p };
          const patch: Partial<typeof s> = { presence: next };
          if (s.user && p.userId === s.user.id) {
            patch.talking = p.talking;
            patch.listening = p.listening;
            patch.feeding = p.feeding;
          }
          return patch;
        });
        this.refreshGains();
      }),
      rpc.on('call', ({ from, target }) => {
        setState((s) => ({ calls: { ...s.calls, [target]: Date.now() + CALL_FLASH_MS, [`user:${from}`]: Date.now() + CALL_FLASH_MS } }));
        if (getState().settings.chimes) this.audio.chime();
        if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
        window.setTimeout(() => this.expireCalls(), CALL_FLASH_MS + 50);
      }),
      rpc.on('chat', ({ message }) => this.onChat(message)),
      rpc.on('config', (cfg) => {
        setState({ serverName: cfg.serverName, user: cfg.user, panel: cfg.panel, channels: cfg.channels, users: cfg.users });
        // Drop latches/listen state for keys that no longer exist.
        const keys = new Set(cfg.panel?.keys.map((k) => k.id) ?? []);
        setState((s) => ({ latched: s.latched.filter((k) => keys.has(k)), armed: s.armed.filter((k) => keys.has(k)) }));
        this.refreshGains();
      }),
      rpc.on('kicked', ({ reason }) => {
        const text =
          reason === 'another-session'
            ? 'Signed in from another device'
            : reason === 'user-deleted'
              ? 'Your account was removed'
              : reason === 'plan-limit'
                ? "This server's plan now allows fewer people online, and you were among the last to join"
                : 'Disconnected by an administrator';
        this.intentionalClose = true;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        setState({ kicked: text });
      }),
    ];
  }

  private async onConsumerNew(ev: { id: string; producerId: string; kind: 'audio'; rtpParameters: unknown; peerId: string; role: 'mic' | 'program'; channelId?: string }): Promise<void> {
    const recvT = this.recvT;
    const rpc = this.rpc;
    if (!recvT || !rpc) return;
    try {
      const consumer = await recvT.consume({ id: ev.id, producerId: ev.producerId, kind: ev.kind, rtpParameters: ev.rtpParameters as ms.RtpParameters });
      this.consumers.set(ev.id, consumer);
      this.audio.addRemote(ev.id, consumer.track, { role: ev.role, channelId: ev.channelId, peerId: ev.peerId }, 0.8);
      setState((s) => ({ consumers: { ...s.consumers, [ev.id]: { peerId: ev.peerId, role: ev.role, channelId: ev.channelId } } }));
      // A route event may already have arrived while consume() was in flight; refreshGains re-applies it.
      this.refreshGains();
      await rpc.call('consumer.ready', { consumerId: ev.id });
    } catch (err) {
      console.error('consume failed', err);
    }
  }

  private dropConsumer(id: string): void {
    this.consumers.get(id)?.close();
    this.consumers.delete(id);
    this.audio.removeRemote(id);
    setState((s) => {
      const consumers = { ...s.consumers };
      const routes = { ...s.routes };
      delete consumers[id];
      delete routes[id];
      return { consumers, routes };
    });
  }

  private onChat(m: ChatMessage): void {
    setState((s) => {
      const chat = [...s.chat, m].slice(-500);
      const me = s.user?.id;
      const thread = m.target.startsWith('user:') && m.from !== me ? `user:${m.from}` : m.target;
      const isOpen = s.drawer === 'chat' && s.chatTarget === thread;
      const unread = m.from === me || isOpen ? s.unread : { ...s.unread, [thread]: (s.unread[thread] ?? 0) + 1 };
      return { chat, unread };
    });
  }

  private expireCalls(): void {
    const now = Date.now();
    setState((s) => {
      const calls: Record<string, number> = {};
      for (const [k, v] of Object.entries(s.calls)) if (v > now) calls[k] = v;
      return { calls };
    });
  }

  /**
   * Volume + IFB ducking for every remote source, derived from routes, key volumes and presence.
   *  mic source     → loudest key that carries any of its routed targets
   *  program source → key volume for its channel, ducked by dimDb while someone else talks on it
   */
  refreshGains(): void {
    const s = getState();
    const panel = s.panel;
    const me = s.user?.id;
    for (const remote of this.audio.remotes.values()) {
      // Server tells us which of our targets this consumer serves (for program feeds: the program
      // channel and/or the IFBs that carry it).
      const targets = s.routes[remote.id] ?? [];
      this.audio.setRouted(remote.id, targets.length > 0);
      let vol = 0;
      for (const key of panel?.keys ?? []) {
        if (key.targets.some((t) => targets.includes(t))) vol = Math.max(vol, keyVolume(key.id));
      }
      if (targets.some((t) => t === `user:${me}`)) vol = Math.max(vol, 0.8);
      this.audio.setVolume(remote.id, vol);
      // Output channel: the first key (in panel order) carrying this source that has one assigned.
      const outKey = (panel?.keys ?? []).find((k) => s.settings.outputMap[k.id] && k.targets.some((t) => targets.includes(t)));
      this.audio.setOutput(remote.id, outKey ? s.settings.outputMap[outKey.id] : 'main');

      // IFB ducking: dim the program feed while someone else talks on an IFB it is routed through.
      let duckDb = 0;
      if (remote.role === 'program') {
        for (const t of targets) {
          const ch = s.channels.find((c) => t === chTarget(c.id));
          if (!ch || ch.kind !== 'ifb') continue;
          const someoneTalking = Object.values(s.presence).some((p) => p.online && p.userId !== me && p.talking.includes(t));
          if (someoneTalking) duckDb = Math.min(duckDb, ch.dimDb);
        }
      }
      this.audio.setDuck(remote.id, duckDb);
    }
  }

  // ---- intercom actions ------------------------------------------------------------------------------

  /** A source wants `targets` talking; only newly-uncovered targets actually key up. */
  private wantTalk(sourceId: string, targets: string[]): void {
    const add: string[] = [];
    for (const t of targets) {
      const set = this.talkSources.get(t) ?? new Set<string>();
      if (set.size === 0) add.push(t);
      set.add(sourceId);
      this.talkSources.set(t, set);
    }
    if (add.length) void this.applyTalk(add, true);
  }

  /** A source no longer wants `targets`; a target only goes quiet once no source wants it. */
  private unwantTalk(sourceId: string, targets: string[]): void {
    const remove: string[] = [];
    for (const t of targets) {
      const set = this.talkSources.get(t);
      if (!set?.delete(sourceId)) continue;
      if (set.size === 0) {
        this.talkSources.delete(t);
        remove.push(t);
      }
    }
    if (remove.length) void this.applyTalk(remove, false);
  }

  /** Coalesce pending wantTalk/unwantTalk calls from the same synchronous tick into one RPC. */
  private talkFlush: Promise<void> = Promise.resolve();
  private flushTalk(): Promise<void> {
    return this.talkFlush;
  }

  private async applyTalk(targets: string[], on: boolean): Promise<void> {
    this.talkFlush = this.talkFlush.then(async () => {
      const rpc = this.rpc;
      if (!rpc) return;
      const allowed = allowedTargets(getState().panel, 'talk');
      const list = targets.filter((t) => allowed.has(t));
      if (!list.length) return;
      // Optimistic: flip local state and the mic producer immediately; the server echoes presence.
      setState((s) => {
        const set = new Set(s.talking);
        for (const t of list) on ? set.add(t) : set.delete(t);
        return { talking: [...set] };
      });
      this.syncMicPause();
      try {
        await rpc.call('talk.set', { targets: list, on });
      } catch (err) {
        console.warn('talk.set failed', err);
      }
    });
    return this.talkFlush;
  }

  private syncMicPause(): void {
    const p = this.micProducer;
    if (!p || p.closed) return;
    const want = getState().talking.length > 0;
    if (want && p.paused) p.resume();
    else if (!want && !p.paused) p.pause();
  }

  /** Momentary talk (press/release). */
  async ptt(key: KeyConfig, on: boolean): Promise<void> {
    if (key.talk === 'none') return;
    void this.audio.ensure();
    if (getState().settings.clicks) this.audio.click(on);
    if (!on && getState().latched.includes(key.id)) return; // still latched by a tap
    const source = `ptt:${key.id}`;
    if (on) this.wantTalk(source, key.targets);
    else this.unwantTalk(source, key.targets);
    await this.flushTalk();
  }

  /** Toggle a latching talk key. */
  async latch(key: KeyConfig): Promise<void> {
    if (key.talk === 'none' || key.talk === 'momentary') return;
    void this.audio.ensure();
    const on = !getState().latched.includes(key.id);
    setState((s) => ({ latched: on ? [...s.latched, key.id] : s.latched.filter((k) => k !== key.id) }));
    if (getState().settings.clicks) this.audio.click(on);
    const source = `latch:${key.id}`;
    if (on) this.wantTalk(source, key.targets);
    else this.unwantTalk(source, key.targets);
    await this.flushTalk();
  }

  /** Release every talk key (Escape) — a panic "all off" that overrides every source, VOX included. */
  async releaseAll(): Promise<void> {
    const targets = getState().talking;
    this.talkSources.clear();
    for (const st of this.voxState.values()) {
      if (st.releaseTimer) clearTimeout(st.releaseTimer);
      st.above = false;
      st.releaseTimer = null;
    }
    setState({ latched: [], continuous: false, centerHeld: false, voxKeys: [] });
    if (targets.length) await this.applyTalk(targets, false);
  }

  // ---- dial: arm keys, centre TALK button, continuous mode ----------------------------------------

  /** Union of talk targets of all armed keys (program channels excluded — they are fed, not talked). */
  armedTargets(): string[] {
    const s = getState();
    const program = new Set(s.channels.filter((c) => c.kind === 'program').map((c) => `ch:${c.id}`));
    const out = new Set<string>();
    for (const key of s.panel?.keys ?? []) {
      if (!s.armed.includes(key.id) || key.talk === 'none') continue;
      for (const t of key.targets) if (!program.has(t)) out.add(t);
    }
    return [...out];
  }

  /** Targets kept on by grid latches (must not be released by dial actions). */
  private latchedTargets(): Set<string> {
    const s = getState();
    const out = new Set<string>();
    for (const key of s.panel?.keys ?? []) if (s.latched.includes(key.id)) for (const t of key.targets) out.add(t);
    return out;
  }

  private centerActive(): boolean {
    const s = getState();
    return s.continuous || s.centerHeld;
  }

  async setArmed(key: KeyConfig, on: boolean): Promise<void> {
    void this.audio.ensure();
    const before = new Set(this.armedTargets());
    setState((s) => ({ armed: on ? [...new Set([...s.armed, key.id])] : s.armed.filter((k) => k !== key.id) }));
    persistArmed(getState().armed);
    if (getState().settings.clicks) this.audio.click(on);
    if (!this.centerActive()) return;
    const after = new Set(this.armedTargets());
    const add = [...after].filter((t) => !before.has(t));
    const remove = [...before].filter((t) => !after.has(t));
    if (add.length) this.wantTalk('dial-center', add);
    if (remove.length) this.unwantTalk('dial-center', remove);
    await this.flushTalk();
  }

  /** Centre button press / release. */
  async center(on: boolean): Promise<void> {
    void this.audio.ensure();
    const targets = this.armedTargets();
    if (on) {
      setState({ centerHeld: true });
      if (getState().settings.clicks) this.audio.click(true);
      if (targets.length) this.wantTalk('dial-center', targets);
      await this.flushTalk();
      return;
    }
    setState({ centerHeld: false });
    if (getState().settings.clicks) this.audio.click(false);
    if (targets.length) this.unwantTalk('dial-center', targets);
    await this.flushTalk();
  }

  /**
   * A hardware PTT button (Bluetooth LE PTT-Z and compatibles) — acts as the dial's centre TALK
   * key, so it talks on whatever keys are armed.
   */
  async hardwarePtt(pressed: boolean): Promise<void> {
    if (getState().continuous) return;
    await this.center(pressed);
  }

  async setContinuous(on: boolean): Promise<void> {
    setState({ continuous: on });
    if (getState().settings.clicks) this.audio.click(on);
    const targets = this.armedTargets();
    if (on) this.wantTalk('dial-center', targets);
    else this.unwantTalk('dial-center', targets);
    await this.flushTalk();
  }

  async toggleProgram(channelId: string): Promise<void> {
    if (getState().feeding.includes(channelId)) await this.stopProgram(channelId);
    else await this.startProgram(channelId);
  }

  async listen(key: KeyConfig, on: boolean): Promise<void> {
    const rpc = this.rpc;
    if (!rpc || key.listen !== 'toggle') return;
    void this.audio.ensure();
    localStorage.setItem(`bccomms.listen.${key.id}`, on ? '1' : '0');
    setState((s) => {
      const set = new Set(s.listening);
      for (const t of key.targets) on ? set.add(t) : set.delete(t);
      return { listening: [...set] };
    });
    await rpc.call('listen.set', { targets: key.targets, on });
  }

  async call(key: KeyConfig): Promise<void> {
    const rpc = this.rpc;
    if (!rpc) return;
    if (getState().settings.clicks) this.audio.click(true);
    await Promise.all(key.targets.map((target) => rpc.call('call.send', { target }).catch(() => undefined)));
  }

  async sendChat(target: string, text: string): Promise<void> {
    await this.rpc?.call('chat.send', { target, text });
  }

  setKeyVolume(keyId: string, volume: number): void {
    updateSettings({ keyVolumes: { ...getState().settings.keyVolumes, [keyId]: volume } });
    this.refreshGains();
  }

  openChat(target: string | null): void {
    setState((s) => {
      const unread = { ...s.unread };
      if (target) delete unread[target];
      return { drawer: 'chat', chatTarget: target ?? s.chatTarget ?? s.panel?.keys[0]?.targets[0] ?? null, unread };
    });
  }

  // ---- program feed (IFB source) ----------------------------------------------------------------------

  async startProgram(channelId: string): Promise<void> {
    const sendT = this.sendT;
    if (!sendT) throw new Error('media not ready');
    if (this.programProducers.has(channelId)) return;
    const stream = await openProgramInput(getState().settings.programDeviceId);
    const track = stream.getAudioTracks()[0];
    if (!track) throw new Error('no audio track');
    const producer = await sendT.produce({
      track,
      codecOptions: { opusStereo: true, opusDtx: false, opusFec: true, opusMaxAverageBitrate: 96_000 },
      appData: { role: 'program', channelId },
    });
    this.programProducers.set(channelId, { producer, stream });
    producer.on('transportclose', () => this.programProducers.delete(channelId));
    setState((s) => ({ feeding: [...new Set([...s.feeding, channelId])] }));
  }

  async stopProgram(channelId: string): Promise<void> {
    const p = this.programProducers.get(channelId);
    if (!p) return;
    this.programProducers.delete(channelId);
    p.producer.close();
    p.stream.getTracks().forEach((t) => t.stop());
    await this.rpc?.call('producer.close', { producerId: p.producer.id }).catch(() => undefined);
    setState((s) => ({ feeding: s.feeding.filter((c) => c !== channelId) }));
  }

  // ---- VOX (voice-activated talk) ---------------------------------------------------------------------

  /**
   * Per-key VOX: while the global toggle is on, any key marked `vox` talks automatically once the
   * mic crosses the threshold, and releases `voxHangMs` after it drops back below — same
   * hysteresis pattern as a hardware VOX gate. Goes through the same wantTalk/unwantTalk
   * arbitration as everything else, so it layers cleanly under a manual PTT or latch on the same
   * key instead of fighting it.
   */
  private voxTick(micDb: number): void {
    const s = getState();
    const panel = s.panel;
    if (!panel) return;
    // No `media === 'ready'` gate: AudioEngine.micLevelDb() already returns -100 (never above any
    // sane threshold) whenever there's no live analyser, so this can't spuriously fire before the
    // mic is up — and it correctly keeps working if the mic becomes ready slightly after this
    // timer starts, rather than waiting for a `media` flag flip that's driven by a separate path.
    const above = s.settings.voxEnabled && micDb > s.settings.voxThreshold;
    for (const key of panel.keys) {
      if (!key.vox || key.talk === 'none') continue;
      let st = this.voxState.get(key.id);
      if (!st) {
        st = { above: false, releaseTimer: null };
        this.voxState.set(key.id, st);
      }
      if (above) {
        if (st.releaseTimer !== null) {
          clearTimeout(st.releaseTimer);
          st.releaseTimer = null;
        }
        if (!st.above) {
          st.above = true;
          setState((s2) => ({ voxKeys: s2.voxKeys.includes(key.id) ? s2.voxKeys : [...s2.voxKeys, key.id] }));
          this.wantTalk(`vox:${key.id}`, key.targets);
        }
      } else if (st.above && st.releaseTimer === null) {
        st.releaseTimer = window.setTimeout(() => {
          st!.above = false;
          st!.releaseTimer = null;
          setState((s2) => ({ voxKeys: s2.voxKeys.filter((k) => k !== key.id) }));
          this.unwantTalk(`vox:${key.id}`, key.targets);
          void this.flushTalk();
        }, s.settings.voxHangMs);
      }
    }
  }

  // ---- housekeeping -------------------------------------------------------------------------------------

  private startTimers(): void {
    this.stopTimers();
    this.meterTimer = window.setInterval(() => {
      const db = this.audio.micLevelDb();
      if (Math.abs(db - getState().micLevelDb) > 0.5) setState({ micLevelDb: db });
      this.voxTick(db);
    }, 80);
    this.pingTimer = window.setInterval(async () => {
      const t = performance.now();
      try {
        await this.rpc?.call('ping', { t: Date.now() });
        setState({ rttMs: Math.round(performance.now() - t) });
      } catch {
        /* ignore */
      }
      this.expireCalls();
    }, 5000);
  }

  private stopTimers(): void {
    if (this.meterTimer) clearInterval(this.meterTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.meterTimer = null;
    this.pingTimer = null;
  }

  private async requestWakeLock(): Promise<void> {
    if (!getState().settings.keepAwake) return;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    if (!nav.wakeLock) return;
    try {
      this.wakeLock = await nav.wakeLock.request('screen');
    } catch {
      /* not allowed in background */
    }
  }

  /** Called on visibilitychange: resume audio + wake lock when the tab comes back. */
  onVisible(): void {
    void this.audio.ensure();
    if (!this.wakeLock) void this.requestWakeLock();
  }

  async applyAudioSettings(): Promise<void> {
    const s = getState().settings;
    if (isNative()) await configureNativeAudio(s.speaker);
    await this.audio.setSink(s.outputDeviceId);
    this.audio.setMultichannel(isDesktop() && s.multichannelOut);
    this.audio.setMasterVolume(s.masterVolume);
    this.audio.setSidetone(s.sidetone);
    this.refreshGains();
  }

  admin<M extends Parameters<RpcClient['call']>[0]>(method: M, params: Parameters<RpcClient['call']>[1]): Promise<unknown> {
    if (!this.rpc) return Promise.reject(new Error('not connected'));
    return this.rpc.call(method, params as never);
  }
}

export const session = new Session();
// Handy for support/debugging from the devtools console.
(window as Window & { bccomms?: Session; bccommsStore?: typeof useStore }).bccomms = session;
(window as Window & { bccommsStore?: typeof useStore }).bccommsStore = useStore;

// ---- keyboard hotkeys: 1–9,0 = momentary talk on keys 1–10; Esc = release all ------------------
const HOTKEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const held = new Set<string>();

function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

let spaceHeld = false;
window.addEventListener('keydown', (e) => {
  if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
  const s = getState();
  if (!s.settings.hotkeys || s.view !== 'panel') return;
  if (e.key === 'Escape') {
    void session.releaseAll();
    return;
  }
  const dial = s.settings.layout === 'dial';
  if (e.key === ' ' && dial) {
    e.preventDefault();
    if (e.repeat || spaceHeld) return;
    spaceHeld = true;
    void session.center(true);
    return;
  }
  const idx = HOTKEYS.indexOf(e.key);
  if (idx < 0 || e.repeat || held.has(e.key)) return;
  const key = s.panel?.keys[idx];
  if (!key || key.talk === 'none') return;
  e.preventDefault();
  if (dial) {
    void session.setArmed(key, !s.armed.includes(key.id));
    return;
  }
  held.add(e.key);
  void session.ptt(key, true);
});
window.addEventListener('keyup', (e) => {
  if (e.key === ' ' && spaceHeld) {
    spaceHeld = false;
    void session.center(false);
    return;
  }
  if (!held.has(e.key)) return;
  held.delete(e.key);
  const key = getState().panel?.keys[HOTKEYS.indexOf(e.key)];
  if (key) void session.ptt(key, false);
});
window.addEventListener('blur', () => {
  if (held.size || spaceHeld) {
    held.clear();
    spaceHeld = false;
    void session.releaseAll();
  }
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') session.onVisible();
});

/** Convenience hook re-export so components only import from one place. */
export { useStore, parseTarget };
